import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Linkedin, Instagram, Phone, Mail } from 'lucide-react';
import { companyInfo, navLinks } from '../data/mock';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <>
      {/* Top Bar with Quick Contact */}
      <div className="bg-[#0a1628] text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            <a href={`tel:${companyInfo.contact.phone}`} className="flex items-center space-x-2 hover:text-[#87CEEB] transition-colors">
              <Phone size={14} />
              <span className="font-medium">{companyInfo.contact.phone}</span>
            </a>
            <a href={`mailto:${companyInfo.contact.email}`} className="flex items-center space-x-2 hover:text-[#87CEEB] transition-colors">
              <Mail size={14} />
              <span className="font-medium">{companyInfo.contact.email}</span>
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 mr-4">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="USA Flag" 
                className="w-5 h-3 object-cover rounded-sm"
              />
              <span className="text-white/80 text-xs">United States</span>
            </div>
            <a href={companyInfo.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-[#87CEEB] transition-colors">
              <Facebook size={16} />
            </a>
            <a href={companyInfo.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-[#87CEEB] transition-colors">
              <Linkedin size={16} />
            </a>
            <a href={companyInfo.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-[#87CEEB] transition-colors">
              <Instagram size={16} />
            </a>
          </div>
        </div>
      </div>

      {/* Header with Logo, Title, and Navigation */}
      <div className="bg-white py-3 border-b border-gray-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Company Name - Centered in Header */}
          <div className="text-center mb-3">
            <h1 className="text-[10px] sm:text-sm md:text-lg lg:text-xl font-medium tracking-[0.1em] sm:tracking-[0.15em] md:tracking-[0.2em] uppercase text-[#1877F2] whitespace-nowrap">
              C DOC CLINICAL RESEARCH ASSOCIATES LLC
            </h1>
          </div>
          
          {/* Actions Row */}
          <div className="flex items-center justify-between">
            {/* Spacer for mobile */}
            <div className="flex-1 lg:hidden"></div>
            {/* Quick Actions - Desktop */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              {/* Navigation will go here */}
            </div>
            <div className="hidden lg:flex items-center space-x-3">
              <a
                href={`tel:${companyInfo.contact.phone}`}
                className="flex items-center space-x-2 bg-[#E6F4FA] text-[#1877F2] px-4 py-2 rounded-full font-semibold text-sm hover:bg-[#1877F2] hover:text-white transition-all duration-300"
                data-testid="call-now-btn"
              >
                <Phone size={16} />
                <span>Call Now</span>
              </a>
              <Link
                to="/contact"
                className="bg-[#1877F2] text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#166FE5] transition-all duration-300 shadow-md hover:shadow-lg"
                data-testid="get-started-btn"
              >
                Get Started
              </Link>
            </div>
            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-[#1877F2] transition-colors rounded-lg hover:bg-gray-100"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
          
          {/* Navigation Row - Desktop */}
          <div className="hidden lg:flex items-center justify-center space-x-8 pt-2 border-t border-gray-100">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-semibold transition-all duration-300 relative group py-2 ${
                  location.pathname === link.path
                    ? 'text-[#1877F2]'
                    : 'text-gray-700 hover:text-[#1877F2]'
                }`}
              >
                {link.name}
                <span className={`absolute bottom-0 left-0 h-0.5 bg-[#1877F2] transition-all duration-300 ${
                  location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className={`lg:hidden overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[600px] pb-4' : 'max-h-0'}`}>
            <div className="flex flex-col space-y-2 pt-3 border-t border-gray-100">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-base font-semibold transition-colors duration-200 py-3 px-4 rounded-lg ${
                    location.pathname === link.path
                      ? 'text-[#1877F2] bg-[#E6F4FA]'
                      : 'text-gray-700 hover:text-[#1877F2] hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Quick Actions */}
              <div className="pt-3 space-y-2 border-t border-gray-100">
                <a
                  href={`tel:${companyInfo.contact.phone}`}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center space-x-2 bg-[#E6F4FA] text-[#1877F2] px-6 py-3 rounded-full font-semibold"
                >
                  <Phone size={18} />
                  <span>Call {companyInfo.contact.phone}</span>
                </a>
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="block bg-[#1877F2] text-white px-6 py-3 rounded-full font-semibold text-center hover:bg-[#166FE5] transition-colors"
                >
                  Get Started
                </Link>
              </div>

              {/* Mobile Social Links */}
              <div className="flex items-center justify-center space-x-6 pt-3 border-t border-gray-100">
                <a href={companyInfo.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-[#1877F2]">
                  <Facebook size={24} />
                </a>
                <a href={companyInfo.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#1877F2]">
                  <Linkedin size={24} />
                </a>
                <a href={companyInfo.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-[#1877F2]">
                  <Instagram size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Large Logo Section - Between Header and Body */}
      <div className="bg-white py-6 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center">
            {/* Large Logo */}
            <Link to="/" className="flex items-center justify-center" data-testid="header-logo">
              <img
                src={companyInfo.logo}
                alt={companyInfo.name}
                className="h-40 sm:h-48 md:h-56 lg:h-64 w-auto object-contain transition-transform duration-300 hover:scale-105"
              />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
